import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { pdfjs, Page, Document } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const PdfDocumentContainer = styled.div`
  position: relative;

  .react-pdf__Page {
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.2);
    margin-bottom: 50px;
  }
`;

/**
 * Renders a PDF document.
 */
const PdfDocument = ({ file, fsMask }) => {
  const [numPages, setNumPages] = useState(0);
  const [width, setWidth] = useState(0);
  const [resizing, setResizing] = useState(false);
  const pdfDocumentRef = useRef(null);

  /**
   * Handles resizing the pdf document viewport.
   */
  const memoizedHandleResize = useCallback(() => {
    if (resizing) return; // acts sort of like a debounce
    if (width === pdfDocumentRef.current?.offsetWidth) return;

    (async () => {
      await setResizing(true);
      window.setTimeout(() => {
        setResizing(false);
        setWidth(pdfDocumentRef.current?.offsetWidth ?? width);
      }, 200);
    })();
  }, [resizing, width, pdfDocumentRef]);

  useEffect(() => {
    window.addEventListener('resize', memoizedHandleResize);
    memoizedHandleResize();

    return () => {
      window.removeEventListener('resize', memoizedHandleResize);
    };
  }, [memoizedHandleResize]);

  /**
   * Handles setting the number of pages when the document loads.
   */
  const handleDocumentLoad = ({ numPages }) => {
    setNumPages(numPages);
  };

  const styles = { display: resizing ? 'none' : 'block' };

  return (
    <PdfDocumentContainer className={fsMask ? 'fs-mask' : ''} ref={pdfDocumentRef}>
      <div style={styles}>
        <Document file={file} onLoadSuccess={handleDocumentLoad}>
          {Array(numPages)
            .fill()
            .map((_, i) => (
              <Page key={`pdf-${i}`} pageNumber={i + 1} width={width} />
            ))}
        </Document>
      </div>
    </PdfDocumentContainer>
  );
};

PdfDocument.propTypes = {
  /** PDF file to display. */
  file: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  /** Toggles masking input within FullStory */
  fsMask: PropTypes.bool,
};

PdfDocument.defaultProps = {
  fsMask: true,
};

export default PdfDocument;
